// Editable single-value combobox: input as trigger, popover with the full
// options list, and free-form typing for values not in the list. Used where a
// curated SKU/plan list exists but the underlying provider catalog is larger
// than what we want to enumerate (e.g. Azure VM SKUs, UpCloud DB plans).
import { useState, useRef, useMemo, useId, forwardRef, type KeyboardEvent } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn, filterComboboxOptions } from '../../../utils';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import type { ComboboxOption } from './Combobox';

export interface EditableComboboxProps {
  /** Available options to suggest. The user may also enter a value not in this list. */
  options: ComboboxOption[];
  /** Current value (may be an option's value OR a custom string the user typed) */
  value: string;
  /** Callback when value changes (typed or selected) */
  onValueChange: (value: string) => void;
  /** Placeholder text when no value is set */
  placeholder?: string;
  /** Text shown when no options match the typed search */
  emptyText?: string;
  /** Additional class for the wrapping div */
  className?: string;
  /** id for the input (label association) */
  id?: string;
  /** Whether the combobox is disabled */
  disabled?: boolean;
}

export const EditableCombobox = forwardRef<HTMLInputElement, EditableComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select or type a value',
      emptyText = 'No matching options. Press Enter to keep your custom value.',
      className,
      id,
      disabled = false,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    // Filter only kicks in once the user has typed since the popover opened.
    // Otherwise opening with a pre-filled value (e.g. a default plan code that
    // exactly matches one option) would hide every other option — the same UX
    // bug the native <input list> + <datalist> had, just dressed differently.
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    // ARIA combobox with a listbox popup (agion/Agion#3041): focus stays in the
    // input, the arrow keys move the active option (aria-activedescendant) and
    // Enter picks it.
    const listboxId = useId();
    const [activeIndex, setActiveIndex] = useState(-1);

    const handleOpenChange = (next: boolean) => {
      if (next) setHasUserTyped(false);
      setActiveIndex(-1);
      setOpen(next);
    };

    // Once the user types, filter via the shared L1 matcher (same fuzzy
    // semantics as table + select search). Before that, show the full
    // list so a pre-filled exact value doesn't hide the alternatives.
    const filteredOptions = useMemo(() => {
      if (!hasUserTyped) return options;
      return filterComboboxOptions(options, value);
    }, [options, value, hasUserTyped]);

    const pick = (option: ComboboxOption) => {
      setHasUserTyped(false);
      onValueChange(option.value);
      setOpen(false);
      setActiveIndex(-1);
      inputRef.current?.focus();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open) {
          handleOpenChange(true);
          return;
        }
        const count = filteredOptions.length;
        if (count === 0) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((current) => (current < 0 ? (step === 1 ? 0 : count - 1) : (current + step + count) % count));
      } else if (e.key === 'Enter' && open) {
        const option = filteredOptions[activeIndex];
        // Without an active option, Enter keeps the typed value (and any form submit).
        if (option) {
          e.preventDefault();
          pick(option);
        }
      }
    };

    const optionId = (index: number) => `${listboxId}-option-${index}`;
    const activeOption = open && activeIndex >= 0 && activeIndex < filteredOptions.length ? optionId(activeIndex) : undefined;

    return (
      <div className={cn('relative', className)}>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            {/* Button wraps the input row so PopoverTrigger has a single child */}
            <div className="relative w-full">
              <input
                ref={(node) => {
                  inputRef.current = node;
                  if (typeof ref === 'function') ref(node);
                  else if (ref) ref.current = node;
                }}
                id={id}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={activeOption}
                aria-autocomplete="list"
                disabled={disabled}
                value={value}
                onChange={(e) => {
                  setHasUserTyped(true);
                  setActiveIndex(-1);
                  onValueChange(e.target.value);
                  if (!open) setOpen(true);
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => handleOpenChange(true)}
                placeholder={placeholder}
                className={cn(
                  'flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 pr-10 text-sm',
                  'placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  'dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-400',
                )}
              />
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={(e) => {
                  // Open popover and refocus the input so typing keeps working
                  e.preventDefault();
                  handleOpenChange(!open);
                  inputRef.current?.focus();
                }}
                aria-label="Toggle options"
                className={cn(
                  'absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center',
                  'rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200',
                )}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            // Keep focus on the input so typing is uninterrupted by the popover
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {filteredOptions.length === 0 ? (
              <div className="max-h-[300px] overflow-y-auto p-1">
                <div className="py-6 px-3 text-center text-sm text-slate-500 dark:text-slate-400">
                  {emptyText}
                </div>
              </div>
            ) : (
              <div id={listboxId} role="listbox" aria-label={placeholder} className="max-h-[300px] overflow-y-auto p-1">
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    id={optionId(index)}
                    role="option"
                    // Focus stays in the input (aria-activedescendant); -1 keeps
                    // the option focusable without adding a tab stop.
                    tabIndex={-1}
                    aria-selected={value === option.value}
                    data-active={index === activeIndex ? 'true' : undefined}
                    onClick={() => pick(option)}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                      'hover:bg-slate-100 hover:text-slate-900',
                      'dark:hover:bg-slate-800 dark:hover:text-slate-50',
                      (value === option.value || index === activeIndex) &&
                        'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50',
                    )}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center gap-2">
                        <span>{option.label}</span>
                        {option.badge && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {option.badge}
                          </span>
                        )}
                      </div>
                      {option.description && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
EditableCombobox.displayName = 'EditableCombobox';
